import { Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import { getDealAllocationData } from 'store/dealManagement/selectors';
import {
  getCampaignImpressionMetrics,
  getSummaryAllocatedFrames,
  getSummaryAllocatedImpressions,
  getSummaryAssets,
  getSummaryAvailableFrames,
  getSummaryAvailableImpressions,
  getSummaryCurrentRemainingImpressions,
  getSummaryDeliveredImpressions,
  getSummaryTotalCost,
} from 'store/dealManagement/selectors/getSummaryAvailability';
import { LevelType } from 'consts/thoughtspot';
import { Asset } from 'components/common/types/Asset.types';
import Preloader from 'lib/Preloader';
import { ProposalCard } from 'components/common/Deal/Cards/ProposalCard/ProposalCard';
import AllocationCard from '../Cards/AllocationCard';
import { getDealSummaryCardVisibility } from '../Cards/selectors';

const DeliveryCard = lazy(() => import('../Cards/DeliveryCard/DeliveryCard'));

const DealLevelCards: React.FC = () => {
  const dealAllocation = useSelector(getDealAllocationData);
  const { isProposalCardVisible, isDeliveryCardVisible } = useSelector(getDealSummaryCardVisibility);

  const allocatedFrames = useSelector(getSummaryAllocatedFrames);
  const allocatedImpressions = useSelector(getSummaryAllocatedImpressions);
  const availableFrames = useSelector(getSummaryAvailableFrames);
  const availableImpressions = useSelector(getSummaryAvailableImpressions);
  const totalCost = useSelector(getSummaryTotalCost);
  const dealSummaryAssets = useSelector(getSummaryAssets) as Asset[];
  const deliveredImpressions = useSelector(getSummaryDeliveredImpressions);
  const remainingImpressions = useSelector(getSummaryCurrentRemainingImpressions);
  const campaignImpressionMetrics = useSelector(getCampaignImpressionMetrics);

  return (
    <div className="flex flex-col gap-3 mb-[12px]">
      <AllocationCard
        allocatedFrames={dealAllocation.allocatedFrames}
        allocatedImpressions={dealAllocation.allocatedImpressions}
        defaultAllocationReportLevelType={LevelType.DEAL}
        totalCost={dealAllocation.totalCost}
      />
      <Suspense
        fallback={
          <div className="relative">
            <Preloader />
          </div>
        }
      >
        {isDeliveryCardVisible && (
          <DeliveryCard
            expectedImpressionsElapsed={campaignImpressionMetrics?.expectedImpressionsElapsed}
            totalAllocatedImpressions={campaignImpressionMetrics?.totalAllocatedImpressions}
            soldImpressions={
              campaignImpressionMetrics?.totalAllocatedImpressions
                ? campaignImpressionMetrics.soldImpressions
                : deliveredImpressions
            }
            remainingImpressions={
              campaignImpressionMetrics?.totalAllocatedImpressions
                ? campaignImpressionMetrics.remainingImpressions
                : remainingImpressions
            }
            osi={campaignImpressionMetrics?.osi}
          />
        )}
      </Suspense>

      <ProposalCard
        allocatedFrames={allocatedFrames}
        allocatedImpressions={allocatedImpressions}
        availableFrames={availableFrames}
        availableImpressions={availableImpressions}
        assets={dealSummaryAssets}
        defaultAllocationReportLevelType={LevelType.DEAL}
        totalCost={totalCost}
        isAvailabilityHidden
        isVisible={isProposalCardVisible}
      />
    </div>
  );
};

export default DealLevelCards;
